// tipoRiesgo.actions.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { NavigateFunction } from "react-router-dom";
import { UseMutationsParams, AlertSeverityEnum } from "@/shared/interfaces";
import { erpApi } from "@/shared/axios";
import { toApiError } from "@/shared/utils/general";
import { useUiStore } from "@/store/ui";
import { getUrlParamsFetch, handleAxiosError } from "@/shared/utils";
import { urlBackend } from "@/shared/constants";
import {
  TipoRiesgo,
  TipoRiesgoForm,
  TipoRiesgoParams,
  TipoRiesgoPagingResp,
} from "@/erp/controlcambios/tipo-riesgo/types/tipoRiesgo.interface";

const { get, post, put, remove, patch } = erpApi();
const BASE = `${urlBackend}api/v1/cambios/tipos-riesgo`;

// listar
export const useFetchGetTiposRiesgo = (
  params?: TipoRiesgoParams,
  enabled = true
) =>
  useQuery({
    queryKey: ["tipos-riesgo", ...Object.values(params ?? {})],
    queryFn: async () => getTiposRiesgo(params),
    enabled,
  });

export const getTiposRiesgo = async (
  params?: TipoRiesgoParams
): Promise<TipoRiesgoPagingResp> => {
  const q = getUrlParamsFetch(params ?? {});
  return (await get<TipoRiesgoPagingResp>(`${BASE}?${q}`, true)) as unknown as TipoRiesgoPagingResp;
};

// obtener por id
export const useFetchGetByIdTipoRiesgo = (id: string) =>
  useQuery({
    queryKey: ["tipo-riesgo", id],
    queryFn: async () => getByIdTipoRiesgo(id),
    refetchOnWindowFocus: false,
  });

export const getByIdTipoRiesgo = async (id: string) =>
  await get<TipoRiesgo>(`${BASE}/${id}`, true);

// crear
export const useFetchPostTipoRiesgo = ({
  returnUrl,
  navigate,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (data: TipoRiesgoForm) => postTipoRiesgo(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipos-riesgo"] });
      navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlerta &&
        setAlerta(
          true,
          customSuccessMessage ?? "Tipo de riesgo creado correctamente",
          AlertSeverityEnum.success
        );
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const postTipoRiesgo = async (data: TipoRiesgoForm) =>
  await post<TipoRiesgo>(BASE, data, true);

// actualizar
export const useFetchPutTipoRiesgo = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: putTipoRiesgo,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tipos-riesgo"] });
      queryClient.invalidateQueries({ queryKey: ["tipo-riesgo", variables.id] });
      setAlerta(true, "Tipo de riesgo actualizado correctamente", AlertSeverityEnum.success);
      navigate(returnUrl);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const putTipoRiesgo = async ({
  id,
  data,
}: {
  id: string;
  data: Omit<TipoRiesgoForm, "tipoRiesgoId">;
}) => await put<TipoRiesgo>(`${BASE}/${id}`, data, true);

// eliminar
export const useFetchDeleteTipoRiesgo = () => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (id: string) => deleteTipoRiesgo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipos-riesgo"] });
      setAlerta(true, "Tipo de riesgo eliminado correctamente", AlertSeverityEnum.success);
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const deleteTipoRiesgo = async (id: string) =>
  await remove<TipoRiesgo>(`${BASE}/${id}`, true);

// cambiar estado
export const useFetchPatchEstadoTipoRiesgo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchEstadoTipoRiesgo,
    onSuccess: () => {
      toast.success("Estado del tipo de riesgo actualizado");
      queryClient.invalidateQueries({ queryKey: ["tipos-riesgo"] });
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const patchEstadoTipoRiesgo = async ({ id, estado }: { id: string; estado: boolean }) =>
  await patch(`${BASE}/${id}/estado?estado=${estado}`, undefined, true);
